// <predictive-search> (§6.10): sugerencias mientras se escribe con el patrón combobox de ARIA:
// flechas para moverse por las opciones (aria-activedescendant), Enter para abrir, Escape para cerrar.
// Usa la búsqueda predictiva de Shopify (window.routes.predictive_search_url + '.json', es decir /search/suggest.json):
// productos y páginas, con la forma resources.results.products[] / pages[] (title, url y, en productos, price «89.00»).
// Con socks_enabled en false (data-socks-enabled), los calcetines no salen. Cada precio lleva «IVA incluido».
const SOCKS = 'calcetines-antideslizantes';

/** Precio de la búsqueda predictiva («89.00», en la moneda de la tienda) → «89,00 €» */
function formatPrice(value) {
  const amount = Number(value);
  if (value == null || value === '' || !Number.isFinite(amount)) return '';
  const [int, dec] = amount.toFixed(2).split('.');
  return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec} €`;
}

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[role="combobox"]');
    this.listbox = this.querySelector('[role="listbox"]');
    this.status = this.querySelector('[data-search-status]');
    this.active = -1;
    let timer;
    this.input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(() => this.suggest(), 200);
    });
    this.input.addEventListener('keydown', (event) => this.onKey(event));
    this.input.addEventListener('blur', () => setTimeout(() => this.close(), 150));
  }

  async suggest() {
    const q = this.input.value.trim();
    if (q.length < 2) return this.close();
    const base = window.routes?.predictive_search_url ?? '/search/suggest';
    try {
      const res = await fetch(`${base}.json?q=${encodeURIComponent(q)}&resources[type]=product,page&resources[limit]=6`, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      // Si mientras tanto se ha seguido escribiendo, esta respuesta ya no vale.
      if (q !== this.input.value.trim()) return;
      const { products = [], pages = [] } = data.resources?.results ?? {};
      const visible = this.dataset.socksEnabled === 'false' ? products.filter((p) => p.handle !== SOCKS) : products;
      this.render([...visible, ...pages]);
    } catch {
      this.close();
    }
  }

  render(items) {
    this.listbox.replaceChildren(...items.map((item, i) => {
      const li = document.createElement('li');
      li.id = `${this.listbox.id}-opcion-${i}`;
      li.setAttribute('role', 'option');
      li.className = 'search__option';
      li.dataset.url = item.url;
      // Sin href: una opción no puede llevar dentro otro control (axe, nested-interactive). Se navega con data-url.
      const a = document.createElement('a');
      // Foto del producto en un marco de lino (decorativa: el título ya lo nombra).
      const src = item.featured_image?.url ?? item.image;
      if (src) {
        const img = document.createElement('img');
        img.className = 'search__option-thumb';
        img.src = `${src}${src.includes('?') ? '&' : '?'}width=96`;
        img.alt = '';
        img.width = 40;
        img.height = 40;
        a.append(img);
      }
      a.append(item.title);
      const price = formatPrice(item.price);
      if (price) {
        const span = document.createElement('span');
        span.className = 'text-small tabular';
        span.textContent = `${price} ${this.dataset.priceVat ?? ''}`.trim();
        a.append(' ', span);
      }
      li.append(a);
      li.addEventListener('mousedown', (event) => {
        event.preventDefault();
        location.href = item.url;
      });
      return li;
    }));
    const open = items.length > 0;
    this.listbox.hidden = !open;
    this.input.setAttribute('aria-expanded', String(open));
    this.active = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.status.textContent = open ? `${items.length} ${items.length === 1 ? 'sugerencia' : 'sugerencias'}` : '';
  }

  close() {
    this.listbox.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.active = -1;
  }

  onKey(event) {
    const options = [...this.listbox.querySelectorAll('[role="option"]')];
    if (event.key === 'Escape') return this.close();
    if (!options.length || this.listbox.hidden) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.active = (this.active + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      options.forEach((o, i) => o.setAttribute('aria-selected', String(i === this.active)));
      this.input.setAttribute('aria-activedescendant', options[this.active].id);
    } else if (event.key === 'Enter' && this.active >= 0) {
      event.preventDefault();
      location.href = options[this.active].dataset.url;
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
